import { colors, Datepicker, Delete, IconButton, spacing, TrashCanWheels } from 'component-library';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { type ArticleGroup, type NewContainer } from '@sr/types';
import { getCountryCodeFromDwkey, isCustomContainerType, isPalletContainerType } from '@sr/utils';
import { getCountry } from '@sr/utils/location';
import { InputField } from 'component-library/components/Input/Text/InputField';
import { ArticleContainerImage } from '../ArticleContainerImage';
import { ContainerPicker } from '../ContainerPicker/ContainerPicker';
import { MaterialPicker } from '../MaterialPicker/MaterialPicker';
import { OtherLoadCarrierPicker } from '../OtherLoadCarrierPicker/OtherLoadCarrierPicker';
import { isOtherMaterial, type ValidatedContainerField } from './containerFields';
import {
  AmountWrapper,
  ContainerFieldsWrapper,
  ContainerImageWrapper,
  ContainerRowCard,
  ContainerRowWrapper,
  DateWrapper,
  GrowingFieldWrapper,
  PalletCollarAmountWrapper,
  StackedDateWrapper,
} from './styles';

// Clamped here, not via Textfield's min, which shows the min value in an empty field.
const toAmount = (value: string, min: number) =>
  value === '' ? undefined : Math.min(Math.max(Number(value), min), 100);

type ContainerRowProps = {
  container: NewContainer;
  errors: Partial<Record<ValidatedContainerField, string>>;
  businessPartnerDwKey: string;
  useGrandParentLocation?: boolean;
  articles: ArticleGroup[];
  isArticlesPending: boolean;
  removeDisabled: boolean;
  onChange: (changes: Partial<NewContainer>) => void;
  onRemove: () => void;
};

export const ContainerRow = ({
  container,
  errors,
  businessPartnerDwKey,
  useGrandParentLocation,
  articles,
  isArticlesPending,
  removeDisabled,
  onChange,
  onRemove,
}: ContainerRowProps) => {
  const { t } = useTranslation();
  const { containerType } = container;
  const isCustomContainer = isCustomContainerType(containerType);
  const isPalletContainer = isPalletContainerType(containerType);
  const date = container.date ? dayjs(container.date).toDate() : null;
  const amountLabelKey = isPalletContainer
    ? 'rented-containers.add-containers.step1.add-container.modal.amount.pallet.label'
    : 'rented-containers.add-containers.step1.add-container.modal.amount.label';

  const datePicker = (
    <Datepicker
      excludeWeekends
      showHolidays
      holidaysCountryCode={getCountryCodeFromDwkey(businessPartnerDwKey)}
      label={t('rented-containers.add-containers.step1.add-container.modal.date.label')}
      placeholderText={t(
        'rented-containers.add-containers.step1.add-container.modal.date.placeholder',
      )}
      onChange={(value) => {
        onChange({ date: value ? dayjs(value).format('YYYY-MM-DD') : undefined });
      }}
      value={date ? date.toString() : ''}
      selected={date}
      minDate={dayjs().toDate()}
      additionalInfoLabel={t(
        'rented-containers.add-containers.step1.add-container.modal.date.highlighted-dates-label',
      )}
      tooltip={t('rented-containers.add-containers.step1.add-container.modal.date.info-tooltip')}
      errorMessage={errors.date}
    />
  );

  return (
    <ContainerRowWrapper>
      <ContainerRowCard>
        <MaterialPicker
          articles={articles}
          isPending={isArticlesPending}
          materialType={container.material}
          errorMessage={errors.material}
          onChange={(material, materialName) => {
            onChange({ material, materialName });
          }}
        />
        {isOtherMaterial(container.material) && (
          <InputField
            label={t(
              'rented-containers.add-containers.step1.add-container.modal.material.other.label',
            )}
            placeholder={t(
              'rented-containers.add-containers.step1.add-container.modal.material.other.placeholder',
            )}
            value={container.materialOther ?? ''}
            errorMessage={errors.materialOther}
            onChange={(event) => {
              onChange({ materialOther: event.target.value });
            }}
          />
        )}
        <ContainerFieldsWrapper>
          <ContainerImageWrapper>
            {containerType && !isCustomContainer ? (
              <ArticleContainerImage
                containerId={containerType}
                isOther={false}
                width={spacing.mediumHigh}
                height={spacing.mediumHigh}
                showMixedCargoIcon={false}
                isOnlyMixedCargo={false}
                country={getCountry(businessPartnerDwKey)}
              />
            ) : (
              <TrashCanWheels
                width={spacing.mediumLow}
                height={spacing.mediumLow}
                color={colors.monochrome.grey50}
              />
            )}
          </ContainerImageWrapper>
          <GrowingFieldWrapper>
            <ContainerPicker
              locationDwKey={businessPartnerDwKey}
              useGrandParentLocation={useGrandParentLocation}
              containerType={containerType}
              errorMessage={errors.containerType}
              onChange={(type, name) => {
                onChange({
                  containerType: type,
                  containerName: name,
                  palletCollarAmount: isPalletContainerType(type)
                    ? container.palletCollarAmount
                    : undefined,
                });
              }}
            />
          </GrowingFieldWrapper>
          {isCustomContainer && (
            <GrowingFieldWrapper>
              <OtherLoadCarrierPicker
                containerType={container.otherLoadCarrierType}
                errorMessage={errors.otherLoadCarrierType}
                onChange={(type, name) => {
                  onChange({ otherLoadCarrierType: type, otherLoadCarrierName: name });
                }}
              />
            </GrowingFieldWrapper>
          )}
          {!isCustomContainer && (
            <AmountWrapper>
              <InputField
                label={t(amountLabelKey)}
                placeholder={t(
                  'rented-containers.add-containers.step1.add-container.modal.amount.placeholder',
                )}
                type="number"
                value={container.amount ?? ''}
                errorMessage={errors.amount}
                onChange={(event) => {
                  onChange({ amount: toAmount(event.target.value, 1) });
                }}
              />
            </AmountWrapper>
          )}
          {isPalletContainer && (
            <PalletCollarAmountWrapper>
              <InputField
                label={t(
                  'rented-containers.add-containers.step1.add-container.modal.pallet-collar-amount.label',
                )}
                placeholder={t(
                  'rented-containers.add-containers.step1.add-container.modal.amount.placeholder',
                )}
                type="number"
                value={container.palletCollarAmount ?? ''}
                onChange={(event) => {
                  onChange({ palletCollarAmount: toAmount(event.target.value, 0) });
                }}
              />
            </PalletCollarAmountWrapper>
          )}
          {!isCustomContainer && !isPalletContainer && <DateWrapper>{datePicker}</DateWrapper>}
        </ContainerFieldsWrapper>
        {isPalletContainer && <StackedDateWrapper>{datePicker}</StackedDateWrapper>}
      </ContainerRowCard>
      <IconButton
        variant="secondary"
        disabled={removeDisabled}
        testId="remove-container-row"
        aria-label={t('rented-containers.add-containers.step1.add-container.container-card.delete')}
        onClick={onRemove}
      >
        <Delete />
      </IconButton>
    </ContainerRowWrapper>
  );
};
